import { useMemo } from 'react'
import { useState } from 'react'
import { SectionHeader, RangeSlider, SelectField, AgronomistNote } from '@/components/ui/CalcUI'

type Cultivar = 'alphonso' | 'tommy' | 'kent' | 'keitt' | 'carabao'

const CULTIVAR_OPTIONS = [
  { value: 'alphonso', label: 'Alphonso (India / Premium)' },
  { value: 'tommy', label: 'Tommy Atkins (US / Commercial)' },
  { value: 'kent', label: 'Kent (Mexico / Export)' },
  { value: 'keitt', label: 'Keitt (Florida / Late Season)' },
  { value: 'carabao', label: 'Carabao (Philippines / Sweet)' },
]

interface BrixProfile {
  minIdealBrix: number
  minHarvestBrix: number
  premium: string
}

const CULTIVAR_BRIX: Record<Cultivar, BrixProfile> = {
  alphonso: { minIdealBrix: 17, minHarvestBrix: 14, premium: 'Extremely sweet. GI-tagged Indian premium. 17–21°Bx ideal for export.' },
  tommy: { minIdealBrix: 11, minHarvestBrix: 10, premium: 'Moderate sweetness. Very firm flesh. 11–14°Bx ideal for shipping.' },
  kent: { minIdealBrix: 14, minHarvestBrix: 12, premium: 'Rich, sweet. 14–16°Bx ideal window. Low fiber.' },
  keitt: { minIdealBrix: 14, minHarvestBrix: 12, premium: 'Large fruit, mild flavor. Stays green when ripe. 14–16°Bx.' },
  carabao: { minIdealBrix: 18, minHarvestBrix: 16, premium: 'World\'s sweetest mango. 18–22°Bx ideal. Very thin seed.' },
}

function getBrixCategory(brix: number): { label: string; color: string; bg: string; use: string; action: string } {
  if (brix < 11) return {
    label: 'Immature / Sour',
    color: '#1d4ed8',
    bg: '#eff6ff',
    use: 'Best for pickles, raw chutney, and green mango products',
    action: 'Do not pick yet. Wait for further maturation on the tree.',
  }
  if (brix <= 14) return {
    label: 'Developing Sweetness',
    color: '#0284c7',
    bg: '#f0f9ff',
    use: 'Ideal commercial shipping stage — firm flesh withstands transport',
    action: 'Harvest immediately for export. Pack and ship within 24 hours.',
  }
  if (brix <= 18) return {
    label: 'Perfect Ripeness',
    color: '#16a34a',
    bg: '#f0fdf4',
    use: 'Ideal local consumption and premium fresh market sales',
    action: 'Harvest for local premium market. Do not delay — use within 3–5 days.',
  }
  return {
    label: 'Ultra-Sweet / Overripe',
    color: '#d97706',
    bg: '#fef3c7',
    use: 'Ideal for purees, juices, smoothies, and processing lines',
    action: 'Ship to puree processing lines immediately. Not suitable for fresh export.',
  }
}

function getSGCategory(sg: number): { label: string; color: string; icon: string; detail: string } {
  if (sg < 1.00) return {
    label: 'Underripe — Floating',
    color: '#1d4ed8',
    icon: '⬆️',
    detail: 'SG < 1.00: Fruit floats in water. Starch not yet converted to sugars. Not ready for harvest.',
  }
  if (sg <= 1.02) return {
    label: 'Mature Green — Ready',
    color: '#16a34a',
    icon: '✅',
    detail: 'SG 1.01–1.02: Fruit sinks slowly. Starch conversion beginning. Ready for harvest and export.',
  }
  return {
    label: 'Fully Ripe — Sinking',
    color: '#d97706',
    icon: '⬇️',
    detail: 'SG > 1.02: Fruit sinks in water. Full sugar development. Great eating quality; process quickly.',
  }
}

// Simple SVG gauge
function BrixGauge({ brix, max = 25 }: { brix: number; max?: number }) {
  const pct = Math.min(brix / max, 1)
  const angle = -135 + pct * 270
  const cx = 100, cy = 90, r = 70
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const tickAngles = [-135, -90, -45, 0, 45, 90, 135]
  const tickLabels = ['5', '8', '11', '14', '17', '20', '25']
  const colors = ['#3b82f6', '#3b82f6', '#0284c7', '#16a34a', '#16a34a', '#d97706', '#ef4444']

  const needleX = cx + (r - 12) * Math.cos(toRad(angle - 90))
  const needleY = cy + (r - 12) * Math.sin(toRad(angle - 90))

  const category = getBrixCategory(brix)

  return (
    <div className="flex flex-col items-center">
      <svg width="200" height="130" viewBox="0 0 200 130">
        {/* Background arc */}
        {tickAngles.map((ang, i) => {
          const startAngle = ang
          const endAngle = tickAngles[i + 1] ?? 135
          if (i >= tickAngles.length - 1) return null
          const startRad = toRad(startAngle - 90)
          const endRad = toRad(endAngle - 90)
          const x1 = cx + r * Math.cos(startRad)
          const y1 = cy + r * Math.sin(startRad)
          const x2 = cx + r * Math.cos(endRad)
          const y2 = cy + r * Math.sin(endRad)
          const largeArc = Math.abs(endAngle - startAngle) > 180 ? 1 : 0
          return (
            <path
              key={i}
              d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`}
              fill="none"
              stroke={colors[i]}
              strokeWidth="12"
              opacity="0.25"
            />
          )
        })}

        {/* Active arc */}
        {(() => {
          const startRad = toRad(-135 - 90)
          const endRad = toRad(angle - 90)
          const x1 = cx + r * Math.cos(startRad)
          const y1 = cy + r * Math.sin(startRad)
          const x2 = cx + r * Math.cos(endRad)
          const y2 = cy + r * Math.sin(endRad)
          const span = angle - (-135)
          const largeArc = span > 180 ? 1 : 0
          return (
            <path
              d={`M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`}
              fill="none"
              stroke={category.color}
              strokeWidth="12"
              strokeLinecap="round"
            />
          )
        })()}

        {/* Ticks */}
        {tickAngles.map((ang, i) => {
          const rad = toRad(ang - 90)
          const lx = cx + (r + 14) * Math.cos(rad)
          const ly = cy + (r + 14) * Math.sin(rad)
          return (
            <text key={i} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fontSize="8" fill="#78716c">
              {tickLabels[i]}
            </text>
          )
        })}

        {/* Needle */}
        <line
          x1={cx}
          y1={cy}
          x2={needleX}
          y2={needleY}
          stroke="#1c1917"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r="5" fill="#1c1917" />

        {/* Value */}
        <text x={cx} y={cy + 22} textAnchor="middle" fontSize="16" fontWeight="800" fill={category.color}>
          {brix.toFixed(1)}°Bx
        </text>
      </svg>
      <div
        className="mt-1 px-4 py-1.5 rounded-full text-sm font-bold"
        style={{ background: category.bg, color: category.color }}
      >
        {category.label}
      </div>
    </div>
  )
}

export default function BrixRipeness() {
  const [brix, setBrix] = useState(14.0)
  const [sg, setSg] = useState(1.01)
  const [cultivar, setCultivar] = useState<Cultivar>('alphonso')

  const brixCat = useMemo(() => getBrixCategory(brix), [brix])
  const sgCat = useMemo(() => getSGCategory(sg), [sg])
  const cultivarProfile = CULTIVAR_BRIX[cultivar]

  const isReadyForIdeal = brix >= cultivarProfile.minIdealBrix
  const isReadyForHarvest = brix >= cultivarProfile.minHarvestBrix

  return (
    <div>
      <SectionHeader
        badge="Calculator 4"
        h2="Mango Brix & Ripeness Quality Control Engine"
        description="Evaluate mango maturity, sweetness, and quality stage using Brix refractometer values and specific gravity measurements."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div
          className="rounded-3xl border p-6"
          style={{ background: '#fff', borderColor: '#e8d9b0' }}
        >
          <h3 className="text-base font-bold mb-4" style={{ color: '#44403c' }}>🔬 Maturity Measurements</h3>

          <RangeSlider
            label="Measured Brix Value"
            value={brix}
            min={5}
            max={25}
            step={0.1}
            onChange={setBrix}
            formatValue={(v) => v.toFixed(1)}
            unit="°Bx"
            tooltip="Brix (°Bx) measures dissolved sugar content. Use a handheld refractometer on fresh mango juice. Higher values indicate greater sweetness and ripeness."
          />

          <RangeSlider
            label="Specific Gravity (SG)"
            value={sg}
            min={0.95}
            max={1.10}
            step={0.01}
            onChange={setSg}
            formatValue={(v) => v.toFixed(2)}
            tooltip="Specific gravity uses water flotation test. SG < 1.00 = floats (unripe). SG 1.01–1.02 = partially sinks (export-ready). SG > 1.02 = fully sinks (ripe)."
          />

          <SelectField
            label="Mango Cultivar"
            value={cultivar}
            options={CULTIVAR_OPTIONS}
            onChange={(v) => setCultivar(v as Cultivar)}
            tooltip="Each cultivar has its own optimal Brix range. Alphonso and Carabao command premium prices at higher Brix. Tommy Atkins is harvested earlier for long-distance shipping."
          />

          {/* Cultivar info */}
          <div
            className="mt-2 p-4 rounded-2xl border"
            style={{ background: '#fef9c3', borderColor: '#fde68a' }}
          >
            <p className="text-xs font-bold uppercase tracking-widest mb-1.5" style={{ color: '#92400e' }}>
              {CULTIVAR_OPTIONS.find((c) => c.value === cultivar)?.label} Profile
            </p>
            <p className="text-sm leading-relaxed" style={{ color: '#78350f' }}>
              {cultivarProfile.premium}
            </p>
            <div className="flex gap-4 mt-3">
              <div className="text-center">
                <p className="text-xs" style={{ color: '#a16207' }}>Min. Harvest</p>
                <p className="text-lg font-bold" style={{ color: '#92400e' }}>{cultivarProfile.minHarvestBrix}°Bx</p>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#a16207' }}>Ideal Range</p>
                <p className="text-lg font-bold" style={{ color: '#92400e' }}>{cultivarProfile.minIdealBrix}°Bx+</p>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: '#a16207' }}>Status</p>
                <p
                  className="text-sm font-bold"
                  style={{ color: isReadyForIdeal ? '#16a34a' : isReadyForHarvest ? '#d97706' : '#dc2626' }}
                >
                  {isReadyForIdeal ? '✅ Ideal' : isReadyForHarvest ? '⚡ Ready' : '⏳ Wait'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="space-y-4">
          {/* Gauge */}
          <div
            className="rounded-3xl border p-5"
            style={{ background: '#fff', borderColor: '#e8d9b0' }}
          >
            <h3 className="text-sm font-bold mb-3 text-center" style={{ color: '#44403c' }}>
              🌡️ Ripeness & Flavor Profile
            </h3>
            <BrixGauge brix={brix} />
          </div>

          {/* SG result */}
          <div
            className="rounded-2xl border p-4"
            style={{ background: sgCat.color === '#d97706' ? '#fff7ed' : sgCat.color === '#16a34a' ? '#f0fdf4' : '#eff6ff', borderColor: '#e8d9b0' }}
          >
            <div className="flex items-center gap-3">
              <span className="text-3xl">{sgCat.icon}</span>
              <div>
                <p className="text-sm font-bold" style={{ color: sgCat.color }}>{sgCat.label}</p>
                <p className="text-xs leading-relaxed mt-1" style={{ color: '#57534e' }}>{sgCat.detail}</p>
              </div>
            </div>
          </div>

          {/* Harvest Action Plan */}
          <div
            className="rounded-3xl border p-5"
            style={{ background: '#fff', borderColor: '#e8d9b0' }}
          >
            <h3 className="text-sm font-bold mb-4" style={{ color: '#44403c' }}>
              📋 Harvest Action Plan
            </h3>
            <div className="space-y-3">
              <div
                className="rounded-2xl p-4 border"
                style={{ background: brixCat.bg, borderColor: brixCat.color + '50' }}
              >
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: brixCat.color }}>
                  Recommended Action
                </p>
                <p className="text-sm font-semibold leading-relaxed" style={{ color: '#1c1917' }}>
                  {brixCat.action}
                </p>
              </div>
              <div
                className="rounded-2xl p-4 border"
                style={{ background: '#fafaf9', borderColor: '#e8d9b0' }}
              >
                <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: '#a16207' }}>
                  Best Use
                </p>
                <p className="text-sm leading-relaxed" style={{ color: '#57534e' }}>
                  {brixCat.use}
                </p>
              </div>
            </div>
          </div>

          <AgronomistNote
            text={`Current Brix of ${brix.toFixed(1)}°Bx + SG of ${sg.toFixed(2)} for ${CULTIVAR_OPTIONS.find(c=>c.value===cultivar)?.label?.split(' ')[0]}: ${brixCat.label}. The water flotation test is a simple, proven non-destructive maturity index used at commercial packhouses globally.`}
            type="info"
          />
        </div>
      </div>
    </div>
  )
}
